import AIChatWidget from '@/components/aichat/AiChatWidget';
import PageHero from '@/components/landing/page-hero';
import SeoHead from '@/components/seo/SeoHead';
import { TablePagination } from '@/components/table-components';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { Download, FileText, Filter, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

type DownloadDocument = {
    title: string;
    category: string;
    fileSize: string;
    lastUpdated: string;
    href: string;
};

const categoryOptions = ['All', 'Policies', 'Forms', 'Handbooks', 'Sustainability', 'Research', 'Reports', 'Academic Documents'] as const;

const documents: DownloadDocument[] = [
    { title: 'Code of Ethics', category: 'Policies', fileSize: '1.2 MB', lastUpdated: '2026-05-12', href: '#' },
    { title: 'Anti-Corruption Policy', category: 'Policies', fileSize: '980 KB', lastUpdated: '2026-04-28', href: '#' },
    { title: 'Conflict of Interest Policy', category: 'Policies', fileSize: '864 KB', lastUpdated: '2026-04-28', href: '#' },
    { title: 'AI Policy', category: 'Policies', fileSize: '742 KB', lastUpdated: '2026-06-10', href: '#' },
    { title: 'Data Privacy Policy', category: 'Policies', fileSize: '1.1 MB', lastUpdated: '2026-03-18', href: '#' },
    { title: 'Integrity & Whistleblowing Guide', category: 'Handbooks', fileSize: '1.5 MB', lastUpdated: '2026-06-15', href: '#' },
    { title: 'Whistleblowing Report Form', category: 'Forms', fileSize: '420 KB', lastUpdated: '2026-06-15', href: '#' },
    { title: 'Academic Calendar 2026', category: 'Academic Documents', fileSize: '560 KB', lastUpdated: '2026-05-30', href: '#' },
    { title: 'Student Handbook', category: 'Handbooks', fileSize: '3.8 MB', lastUpdated: '2026-01-22', href: '#' },
    { title: 'Faculty Research Publication Index', category: 'Research', fileSize: '2.1 MB', lastUpdated: '2026-06-01', href: '#' },
    { title: 'Sustainability Progress Summary', category: 'Sustainability', fileSize: '1.9 MB', lastUpdated: '2026-06-07', href: '#' },
    { title: 'Institutional Governance Report', category: 'Reports', fileSize: '2.5 MB', lastUpdated: '2026-06-14', href: '#' },
];

const perPage = 6;

function parsePage(value: string | null): number {
    const page = Number.parseInt(value ?? '1', 10);

    return Number.isNaN(page) || page < 1 ? 1 : page;
}

export default function Downloads() {
    const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const [query, setQuery] = useState(searchParams.get('query') ?? '');
    const [category, setCategory] = useState(searchParams.get('category') ?? 'All');
    const [page, setPage] = useState(parsePage(searchParams.get('page')));

    const filteredDocuments = useMemo(() => {
        return documents.filter((document) => {
            const matchesCategory = category === 'All' || document.category === category;
            const normalizedQuery = query.trim().toLowerCase();
            const matchesQuery =
                normalizedQuery.length === 0 ||
                document.title.toLowerCase().includes(normalizedQuery) ||
                document.category.toLowerCase().includes(normalizedQuery);

            return matchesCategory && matchesQuery;
        });
    }, [category, query]);

    const lastPage = Math.max(1, Math.ceil(filteredDocuments.length / perPage));

    useEffect(() => {
        if (page > lastPage) {
            setPage(lastPage);
        }
    }, [lastPage, page]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const params = new URLSearchParams();

        if (query.trim()) {
            params.set('query', query.trim());
        }

        if (category !== 'All') {
            params.set('category', category);
        }

        if (page > 1) {
            params.set('page', String(page));
        }

        const href = params.toString() ? `/downloads?${params.toString()}` : '/downloads';

        if (href !== `${window.location.pathname}${window.location.search}`) {
            window.history.replaceState(window.history.state, '', href);
        }
    }, [category, page, query]);

    const currentPage = Math.min(page, lastPage);
    const paginatedDocuments = filteredDocuments.slice((currentPage - 1) * perPage, currentPage * perPage);
    const from = filteredDocuments.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const to = filteredDocuments.length === 0 ? 0 : Math.min(filteredDocuments.length, currentPage * perPage);

    return (
        <LandingLayout>
            <SeoHead
                title="Download Center"
                description="Browse policies, forms, handbooks, reports, and academic documents from Northeastern College."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                <PageHero
                    title="Download Center"
                    description="Search and browse public institutional documents, policies, forms, handbooks, research references, and reports."
                    breadcrumbs={[
                        { label: 'Home', href: '/' },
                        { label: 'About' },
                        { label: 'Governance', href: '/governance' },
                        { label: 'Download Center' },
                    ]}
                    image="/images/hero/course.png"
                    imageAlt="Northeastern College download center"
                    icon={Download}
                />

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl space-y-8 px-4">
                    <Card className="border-border rounded-2xl py-0 shadow-sm">
                        <CardContent className="grid gap-4 p-6 lg:grid-cols-[1.1fr_0.9fr]">
                            <div className="space-y-3">
                                <label htmlFor="download-search" className="text-foreground text-sm font-bold">
                                    Search documents
                                </label>
                                <div className="relative">
                                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" aria-hidden="true" />
                                    <Input
                                        id="download-search"
                                        value={query}
                                        onChange={(event) => {
                                            setQuery(event.target.value);
                                            setPage(1);
                                        }}
                                        placeholder="Search by title or category"
                                        className="pl-10"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm font-bold">
                                    <Filter className="h-4 w-4 text-[#0A7A35]" aria-hidden="true" />
                                    Category filters
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {categoryOptions.map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            onClick={() => {
                                                setCategory(option);
                                                setPage(1);
                                            }}
                                            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                                                category === option
                                                    ? 'border-[#0A7A35] bg-[#0A7A35] text-white'
                                                    : 'border-border bg-background text-muted-foreground hover:border-emerald-500/20 hover:text-foreground'
                                            }`}
                                            aria-pressed={category === option}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {paginatedDocuments.map((document) => (
                            <Card key={`${document.category}-${document.title}`} className="border-border rounded-2xl py-0 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md">
                                <CardContent className="flex h-full flex-col gap-5 p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0A7A35]/10 text-[#0A7A35] dark:text-[#21d36b]">
                                            <FileText className="h-6 w-6" aria-hidden="true" />
                                        </div>
                                        <div className="space-y-2">
                                            <h2 className="text-foreground text-lg font-bold">{document.title}</h2>
                                            <div className="inline-flex rounded-full bg-[#0A7A35]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#0A7A35] uppercase dark:text-[#21d36b]">
                                                {document.category}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid gap-3 text-sm">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-muted-foreground">File size</span>
                                            <span className="text-foreground font-semibold">{document.fileSize}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-muted-foreground">Last updated</span>
                                            <span className="text-foreground font-semibold">{document.lastUpdated}</span>
                                        </div>
                                    </div>

                                    <div className="mt-auto">
                                        <Button asChild className="w-full rounded-full bg-[#00963f] text-white hover:bg-[#008538]">
                                            <Link href={document.href} aria-label={`Download ${document.title}`}>
                                                <Download className="h-4 w-4" />
                                                Download
                                            </Link>
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {paginatedDocuments.length === 0 ? (
                        <Card className="border-border rounded-2xl py-0 shadow-sm">
                            <CardContent className="p-6">
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    No documents match your current search and filter combination. Try a different keyword or switch categories.
                                </p>
                            </CardContent>
                        </Card>
                    ) : null}

                    <Card className="border-border rounded-2xl py-0 shadow-sm">
                        <CardContent className="p-6">
                            <TablePagination
                                hidePerPage
                                currentPage={currentPage}
                                lastPage={lastPage}
                                perPage={perPage}
                                total={filteredDocuments.length}
                                from={from}
                                to={to}
                                onPageChange={(nextPage) => setPage(nextPage)}
                                onPerPageChange={() => {}}
                            />
                        </CardContent>
                    </Card>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
